import React, { useState, useEffect } from 'react';
import { BarChart3, Users, BookOpen, TrendingUp, Award, Download, Headphones, Mic, PenTool, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ELDBadge, KLUBadge } from '@/components/StandardsBadges';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY_COLORS = {
  listening: '#002FA7',
  speaking: '#3b82f6',
  reading: '#22c55e',
  writing: '#f59e0b',
  overall: '#6b7280',
};

const MODALITY_ICONS = {
  listening: Headphones,
  speaking: Mic,
  reading: BookOpen,
  writing: PenTool,
};

const ModalityTrendChart = ({ trends }) => {
  if (!trends || trends.length < 2) return null;

  const modalities = ['listening', 'speaking', 'reading', 'writing'];
  const allVals = modalities.flatMap(m => trends.map(t => t[`avg_${m}`])).filter(v => v > 0);
  const minVal = Math.max(Math.min(...allVals) - 0.3, 0);
  const maxVal = Math.min(Math.max(...allVals) + 0.3, 6);
  const range = maxVal - minVal || 1;

  const W = 600;
  const H = 200;
  const padL = 36;
  const padR = 16;
  const padT = 12;
  const padB = 36;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const getX = (i) => padL + (i / (trends.length - 1)) * chartW;
  const getY = (val) => padT + chartH - ((val - minVal) / range) * chartH;

  // Y-axis grid lines
  const gridLines = [];
  for (let v = Math.ceil(minVal); v <= Math.floor(maxVal); v++) {
    gridLines.push(v);
  }

  return (
    <div className="overflow-x-auto">
      <svg width={W} height={H} className="w-full" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" data-testid="modality-trend-chart">
        {/* Grid */}
        {gridLines.map(v => (
          <g key={v}>
            <line x1={padL} y1={getY(v)} x2={W - padR} y2={getY(v)} stroke="#e5e7eb" strokeWidth="1" />
            <text x={padL - 4} y={getY(v) + 4} textAnchor="end" fill="#9ca3af" fontSize="10">{v.toFixed(0)}</text>
          </g>
        ))}
        {/* X labels */}
        {trends.map((t, i) => (
          <text key={i} x={getX(i)} y={H - 6} textAnchor="middle" fill="#9ca3af" fontSize="9">
            {t.label.length > 7 ? t.label.slice(5) : t.label}
          </text>
        ))}
        {/* Lines */}
        {modalities.map(mod => {
          const points = trends.map((t, i) => `${getX(i)},${getY(t[`avg_${mod}`])}`).join(' ');
          return (
            <g key={mod}>
              <polyline
                points={points}
                fill="none"
                stroke={MODALITY_COLORS[mod]}
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {trends.map((t, i) => (
                <circle key={i} cx={getX(i)} cy={getY(t[`avg_${mod}`])} r="3.5" fill={MODALITY_COLORS[mod]} />
              ))}
            </g>
          );
        })}
      </svg>
      {/* Legend */}
      <div className="flex justify-center gap-5 mt-2">
        {modalities.map(mod => {
          const Icon = MODALITY_ICONS[mod];
          return (
            <div key={mod} className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
              <Icon size={12} style={{ color: MODALITY_COLORS[mod] }} />
              <span className="capitalize">{mod}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [byGrade, setByGrade] = useState([]);
  const [byModality, setByModality] = useState({});
  const [distribution, setDistribution] = useState([]);
  const [modalityTrends, setModalityTrends] = useState(null);
  const [standardsData, setStandardsData] = useState(null);
  const [viewMode, setViewMode] = useState('modality');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [ov, gr, mod, dist, trends, stds] = await Promise.all([
          axios.get(`${API}/analytics/overview`),
          axios.get(`${API}/analytics/by-grade`),
          axios.get(`${API}/analytics/by-modality`),
          axios.get(`${API}/analytics/wida-distribution`),
          axios.get(`${API}/analytics/modality-trends`),
          axios.get(`${API}/analytics/by-eld-standard`).catch(() => ({ data: null })),
        ]);
        setOverview(ov.data);
        setByGrade(gr.data.grades || []);
        setByModality(mod.data.modalities || {});
        setDistribution(dist.data.levels || []);
        setModalityTrends(trends.data);
        setStandardsData(stds.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) return <div className="p-8 text-center text-gray-400">Loading analytics...</div>;

  const totalDist = distribution.reduce((s, l) => s + l.count, 0);
  const levelColors = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#22c55e', 5: '#3b82f6', 6: '#8b5cf6' };

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header with View Toggle and Export */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2" data-testid="analytics-view-toggle">
          <Button
            variant={viewMode === 'modality' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('modality')}
            className={`btn-sharp ${viewMode === 'modality' ? 'bg-[#002FA7] text-white' : ''}`}
            data-testid="view-modality-btn"
          >
            <BarChart3 size={14} className="mr-1.5" />
            By Modality
          </Button>
          <Button
            variant={viewMode === 'standards' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('standards')}
            className={`btn-sharp ${viewMode === 'standards' ? 'bg-[#002FA7] text-white' : ''}`}
            data-testid="view-standards-btn"
          >
            <Layers size={14} className="mr-1.5" />
            By ELD Standard
          </Button>
        </div>
        <Button
          variant="outline"
          className="btn-sharp h-10"
          onClick={() => window.open(`${API}/export/analytics`, '_blank')}
          data-testid="export-analytics-csv"
        >
          <Download size={16} className="mr-2" /> Export Analytics CSV
        </Button>
      </div>
      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { icon: Users, label: 'Students', value: overview?.total_students || 0, color: 'text-blue-600' },
          { icon: BookOpen, label: 'Exams Taken', value: overview?.total_exams || 0, color: 'text-green-600' },
          { icon: TrendingUp, label: 'Completion Rate', value: `${overview?.completion_rate || 0}%`, color: 'text-purple-600' },
          { icon: Award, label: 'Avg Proficiency Score', value: overview?.avg_overall || 0, color: 'text-orange-600' },
          { icon: BarChart3, label: 'Total Results', value: overview?.total_results || 0, color: 'text-red-600' },
        ].map(({ icon: Icon, label, value, color }) => (
          <div key={label} className="border-2 border-gray-200 p-4">
            <Icon className={`w-5 h-5 mb-2 ${color}`} />
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {/* WIDA Level Distribution */}
      <div className="border-2 border-gray-200 p-5" data-testid="wida-distribution">
        <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-4">Proficiency Level Distribution</h3>
        {distribution.length === 0 ? (
          <p className="text-gray-400 text-sm">No results yet to display distribution.</p>
        ) : (
          <div className="space-y-3">
            {distribution.map(l => {
              const pct = totalDist > 0 ? (l.count / totalDist * 100) : 0;
              return (
                <div key={l.level} className="flex items-center gap-3">
                  <div className="w-28 text-sm font-medium">L{l.level} {l.name}</div>
                  <div className="flex-1 bg-gray-100 h-7 relative overflow-hidden">
                    <div
                      className="h-full transition-all duration-700"
                      style={{ width: `${pct}%`, backgroundColor: levelColors[l.level] || '#6b7280' }}
                    />
                    <span className="absolute inset-0 flex items-center px-2 text-xs font-semibold">
                      {l.count} ({pct.toFixed(0)}%)
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ Standards-Based View ═══ */}
      {viewMode === 'standards' && standardsData && (
        <>
          {/* ELD Standard Performance */}
          <div className="border-2 border-gray-200 p-5" data-testid="eld-standard-performance">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-1">Performance by ELD Standard</h3>
            <p className="text-xs text-gray-400 mb-4">
              Average score across all responses mapped to each of WIDA's 5 ELD Standards ({standardsData.total_responses_analyzed?.toLocaleString()} responses analyzed)
            </p>
            <div className="space-y-4">
              {standardsData.by_eld_standard.map(std => {
                const barColor = std.avg_score >= 75 ? '#22c55e' : std.avg_score >= 50 ? '#eab308' : '#ef4444';
                return (
                  <div key={std.standard} data-testid={`eld-std-row-${std.standard}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <ELDBadge standard={std.standard} />
                        <span className="text-xs text-gray-400">({std.response_count} responses)</span>
                      </div>
                      <span className="font-bold text-sm">{std.avg_score}%</span>
                    </div>
                    <div className="bg-gray-100 h-5 relative overflow-hidden">
                      <div
                        className="h-full transition-all duration-700"
                        style={{ width: `${std.avg_score}%`, backgroundColor: barColor }}
                      />
                    </div>
                    {/* Per-modality sub-bars */}
                    {Object.keys(std.modality_scores || {}).length > 0 && (
                      <div className="flex gap-3 mt-2 ml-4 flex-wrap">
                        {['listening', 'speaking', 'reading', 'writing'].map(mod => {
                          const val = std.modality_scores[mod];
                          if (val === undefined) return null;
                          const Icon = MODALITY_ICONS[mod];
                          return (
                            <div key={mod} className="flex items-center gap-1 text-xs">
                              {Icon && <Icon size={12} style={{ color: MODALITY_COLORS[mod] }} />}
                              <span className="capitalize text-gray-500">{mod}:</span>
                              <span className="font-semibold" style={{ color: MODALITY_COLORS[mod] }}>{val}%</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Key Language Use Performance */}
          <div className="border-2 border-gray-200 p-5" data-testid="klu-performance">
            <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-1">Performance by Key Language Use</h3>
            <p className="text-xs text-gray-400 mb-4">
              How students perform across the 4 Key Language Uses (Narrate, Inform, Explain, Argue)
            </p>
            <div className="grid md:grid-cols-4 gap-4">
              {standardsData.by_klu.map(klu => {
                const barColor = klu.avg_score >= 75 ? '#22c55e' : klu.avg_score >= 50 ? '#eab308' : '#ef4444';
                return (
                  <div key={klu.klu} className="border border-gray-200 p-4" data-testid={`klu-card-${klu.klu}`}>
                    <div className="flex items-center justify-between mb-2">
                      <KLUBadge klu={klu.klu} />
                    </div>
                    <div className="text-3xl font-bold mb-1" style={{ color: barColor }}>{klu.avg_score}%</div>
                    <div className="text-xs text-gray-400 mb-2">{klu.response_count} responses</div>
                    <div className="bg-gray-100 h-2 overflow-hidden">
                      <div className="h-full transition-all duration-700" style={{ width: `${klu.avg_score}%`, backgroundColor: barColor }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Cross-Tab: ELD Standard x Modality heatmap */}
          {standardsData.cross_matrix?.length > 0 && (
            <div className="border-2 border-gray-200 p-5" data-testid="eld-modality-heatmap">
              <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-1">ELD Standard x Modality Heatmap</h3>
              <p className="text-xs text-gray-400 mb-4">
                Performance matrix showing how students score on each modality within each ELD Standard
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-2 text-xs font-semibold text-gray-500 uppercase pr-4">ELD Standard</th>
                      <th className="text-center py-2 text-xs font-semibold uppercase" style={{ color: MODALITY_COLORS.listening }}>Listening</th>
                      <th className="text-center py-2 text-xs font-semibold uppercase" style={{ color: MODALITY_COLORS.speaking }}>Speaking</th>
                      <th className="text-center py-2 text-xs font-semibold uppercase" style={{ color: MODALITY_COLORS.reading }}>Reading</th>
                      <th className="text-center py-2 text-xs font-semibold uppercase" style={{ color: MODALITY_COLORS.writing }}>Writing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standardsData.cross_matrix.map(row => (
                      <tr key={row.standard} className="border-b border-gray-100">
                        <td className="py-3 pr-4">
                          <ELDBadge standard={row.standard} size="xs" />
                        </td>
                        {['listening', 'speaking', 'reading', 'writing'].map(mod => {
                          const val = row[mod];
                          const count = row[`${mod}_count`] || 0;
                          if (val === null || val === undefined) {
                            return <td key={mod} className="py-3 text-center text-gray-300 text-xs">No data</td>;
                          }
                          const bg = val >= 75 ? 'bg-green-100 text-green-800' : val >= 60 ? 'bg-yellow-100 text-yellow-800' : val >= 45 ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800';
                          return (
                            <td key={mod} className="py-3 text-center">
                              <span className={`inline-block px-3 py-1 font-bold text-sm ${bg}`} title={`${count} responses`}>
                                {val}%
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3 mt-4 text-xs text-gray-500">
                <span className="font-medium">Legend:</span>
                <span className="inline-block px-2 py-0.5 bg-green-100 text-green-800">75%+</span>
                <span className="inline-block px-2 py-0.5 bg-yellow-100 text-yellow-800">60-74%</span>
                <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800">45-59%</span>
                <span className="inline-block px-2 py-0.5 bg-red-100 text-red-800">&lt;45%</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ Modality-Based View (existing) ═══ */}
      {viewMode === 'modality' && (
        <>
      {/* Modality Growth Over Time */}
      {modalityTrends?.trends?.length >= 2 && (
        <div className="border-2 border-gray-200 p-5" data-testid="modality-trends">
          <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-1">Modality Growth Over Time</h3>
          <p className="text-xs text-gray-400 mb-4">
            Average proficiency scores per modality across {modalityTrends.group_by === 'window' ? 'assessment windows' : 'months'}
          </p>
          <ModalityTrendChart trends={modalityTrends.trends} />
          {/* Numeric trend table */}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-semibold">Period</th>
                  <th className="text-center py-2 font-semibold" style={{ color: MODALITY_COLORS.listening }}>Listening</th>
                  <th className="text-center py-2 font-semibold" style={{ color: MODALITY_COLORS.speaking }}>Speaking</th>
                  <th className="text-center py-2 font-semibold" style={{ color: MODALITY_COLORS.reading }}>Reading</th>
                  <th className="text-center py-2 font-semibold" style={{ color: MODALITY_COLORS.writing }}>Writing</th>
                  <th className="text-center py-2 text-gray-500 font-semibold">Overall</th>
                  <th className="text-center py-2 text-gray-500 font-semibold">N</th>
                </tr>
              </thead>
              <tbody>
                {modalityTrends.trends.map((t, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-2 font-medium">{t.label}</td>
                    <td className="py-2 text-center">{t.avg_listening}</td>
                    <td className="py-2 text-center">{t.avg_speaking}</td>
                    <td className="py-2 text-center">{t.avg_reading}</td>
                    <td className="py-2 text-center">{t.avg_writing}</td>
                    <td className="py-2 text-center font-bold">{t.avg_overall}</td>
                    <td className="py-2 text-center text-gray-400">{t.result_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modality Performance */}
      <div className="border-2 border-gray-200 p-5" data-testid="modality-performance">
        <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-4">Performance by Modality</h3>
        <div className="grid md:grid-cols-4 gap-4">
          {['listening', 'speaking', 'reading', 'writing'].map(m => {
            const data = byModality[m] || { avg: 0, min: 0, max: 0 };
            const modalityColors = { listening: '#002FA7', speaking: '#3b82f6', reading: '#22c55e', writing: '#f59e0b' };
            return (
              <div key={m} className="border border-gray-200 p-4 text-center">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">{m}</div>
                <div className="text-3xl font-bold" style={{ color: modalityColors[m] }}>{data.avg}</div>
                <div className="text-xs text-gray-400 mt-1">Range: {data.min} - {data.max}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Grade-Level Breakdown */}
      <div className="border-2 border-gray-200 p-5" data-testid="grade-breakdown">
        <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-4">Results by Grade Level</h3>
        {byGrade.length === 0 ? (
          <p className="text-gray-400 text-sm">No grade-level data available yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-2 text-xs font-semibold text-gray-500 uppercase">Grade</th>
                <th className="text-center py-2 text-xs font-semibold text-gray-500 uppercase">Exams</th>
                <th className="text-center py-2 text-xs font-semibold text-gray-500 uppercase">Avg Score</th>
                <th className="text-center py-2 text-xs font-semibold text-gray-500 uppercase">Listening</th>
                <th className="text-center py-2 text-xs font-semibold text-gray-500 uppercase">Speaking</th>
                <th className="text-center py-2 text-xs font-semibold text-gray-500 uppercase">Reading</th>
                <th className="text-center py-2 text-xs font-semibold text-gray-500 uppercase">Writing</th>
              </tr>
            </thead>
            <tbody>
              {byGrade.map(g => (
                <tr key={g.grade_level} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 font-semibold">Grade {g.grade_level}</td>
                  <td className="py-3 text-center">{g.count}</td>
                  <td className="py-3 text-center font-bold">{g.avg_score}</td>
                  <td className="py-3 text-center">{g.avg_listening}</td>
                  <td className="py-3 text-center">{g.avg_speaking}</td>
                  <td className="py-3 text-center">{g.avg_reading}</td>
                  <td className="py-3 text-center">{g.avg_writing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Close modality view block */}
        </>
      )}

      {/* Average Domain Scores (visible in both views) */}
      {overview && (
        <div className="border-2 border-gray-200 p-5" data-testid="avg-domain-scores">
          <h3 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-4">Overall Domain Averages</h3>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { label: 'Listening', val: overview.avg_listening, color: '#002FA7' },
              { label: 'Speaking', val: overview.avg_speaking, color: '#3b82f6' },
              { label: 'Reading', val: overview.avg_reading, color: '#22c55e' },
              { label: 'Writing', val: overview.avg_writing, color: '#f59e0b' },
            ].map(({ label, val, color }) => (
              <div key={label} className="text-center">
                <div className="relative w-20 h-20 mx-auto mb-2">
                  <svg className="w-20 h-20" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="34" fill="none" stroke="#e5e7eb" strokeWidth="6" />
                    <circle cx="40" cy="40" r="34" fill="none" stroke={color} strokeWidth="6"
                      strokeDasharray={`${(val / 6) * 213.6} 213.6`}
                      strokeLinecap="round" transform="rotate(-90 40 40)" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center font-bold text-lg">{val}</div>
                </div>
                <div className="text-xs font-semibold text-gray-500 uppercase">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
